'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const steps = ['Vehicle', 'Service', 'Schedule'];

const serviceTiers = [
  {
    id: 'interior',
    name: 'Interior Detail',
    price: 'From $75',
    duration: '1 hr',
    desc: 'Full vacuum, deep clean, dashboard wipe-down, window cleaning, seat & carpet cleaning.',
  },
  {
    id: 'exterior',
    name: 'Exterior Detail',
    price: 'From $50',
    duration: '30 min',
    desc: 'Hand wash, clay bar, paint sealant, tire dressing, window polish.',
  },
  {
    id: 'full',
    name: 'Full Detail Package',
    price: 'From $110',
    duration: '1.5–2 hrs',
    desc: 'Complete interior + exterior detail. Best value for a full refresh.',
  },
];

export default function BookingModal({ isOpen, onClose }: BookingModalProps) {
  const [step, setStep] = useState(0);
  const [vehicle, setVehicle] = useState({ make: '', model: '', year: '' });
  const [selectedService, setSelectedService] = useState('');
  const [date, setDate] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (step < 2) { setStep(step + 1); return; }
    setSubmitted(true);
  };

  const canProceed = () => {
    if (step === 0) return vehicle.make && vehicle.model;
    if (step === 1) return selectedService !== '';
    if (step === 2) return date !== '';
    return false;
  };

  return (
    <div className={`modal-overlay ${isOpen ? 'open' : ''}`} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="modal-panel w-full max-w-lg mx-4 rounded-2xl overflow-hidden"
        style={{ background: '#1C1C1E', border: '1px solid rgba(212,212,216,0.12)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-5 border-b"
          style={{ borderColor: 'rgba(212,212,216,0.1)', background: '#111113' }}
        >
          <div>
            <p className="mono text-[10px] tracking-widest uppercase" style={{ color: '#2D5F9A' }}>
              Reserve Your Bay
            </p>
            <p className="text-lg font-semibold text-off-white mt-0.5">
              {submitted ? 'You\'re Confirmed' : steps[step]}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full transition-colors hover:bg-white/5">
            <Icon name="XMarkIcon" size={20} variant="outline" className="text-aluminum" />
          </button>
        </div>

        {/* Step indicator */}
        {!submitted && (
          <div className="flex px-6 pt-4 gap-2">
            {steps.map((s, i) => (
              <div key={s} className="flex-1 flex flex-col gap-1">
                <div
                  className="h-0.5 rounded-full transition-all duration-500"
                  style={{ background: i <= step ? '#2D5F9A' : 'rgba(168,169,173,0.2)' }}
                />
                <span className="mono text-[9px] tracking-widest uppercase" style={{ color: i === step ? '#D4D4D8' : 'rgba(168,169,173,0.35)' }}>
                  {s}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: 'rgba(45,95,154,0.15)', border: '1px solid rgba(45,95,154,0.3)' }}
              >
                <Icon name="CheckIcon" size={28} variant="outline" className="text-sapphire" />
              </div>
              <p className="text-xl font-semibold text-off-white mb-2">Bay Reserved</p>
              <p className="text-sm" style={{ color: '#A8A9AD' }}>
                We&apos;ll send a confirmation text with drop-off instructions. See you {date || 'soon'}.
              </p>
              <button
                onClick={onClose}
                className="mt-6 btn-sapphire px-8 py-3 rounded-full mono text-[11px] font-semibold tracking-widest uppercase text-white"
              >
                Done
              </button>
            </div>
          ) : step === 0 ? (
            <div className="space-y-4">
              <p className="text-sm" style={{ color: '#A8A9AD' }}>Tell us about your vehicle so we can prepare the right setup.</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'make', placeholder: 'Make (e.g. Porsche)' },
                  { key: 'model', placeholder: 'Model (e.g. Cayenne)' },
                ].map(({ key, placeholder }) => (
                  <input
                    key={key}
                    type="text"
                    placeholder={placeholder}
                    value={vehicle[key as keyof typeof vehicle]}
                    onChange={(e) => setVehicle({ ...vehicle, [key]: e.target.value })}
                    className="px-4 py-3 rounded-lg text-sm text-off-white placeholder-aluminum/40 outline-none transition-colors focus:ring-1"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(212,212,216,0.12)',
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(45,95,154,0.6)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(212,212,216,0.12)')}
                  />
                ))}
              </div>
              <input
                type="text"
                placeholder="Year (e.g. 2023)"
                value={vehicle.year}
                onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })}
                className="w-full px-4 py-3 rounded-lg text-sm text-off-white placeholder-aluminum/40 outline-none"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(212,212,216,0.12)',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(45,95,154,0.6)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(212,212,216,0.12)')}
              />
            </div>
          ) : step === 1 ? (
            <div className="space-y-3">
              <p className="text-sm mb-4" style={{ color: '#A8A9AD' }}>Select your service tier.</p>
              {serviceTiers.map((tier) => (
                <button
                  key={tier.id}
                  onClick={() => setSelectedService(tier.id)}
                  className="w-full text-left p-4 rounded-xl transition-all duration-200"
                  style={{
                    background: selectedService === tier.id ? 'rgba(45,95,154,0.15)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${selectedService === tier.id ? 'rgba(45,95,154,0.5)' : 'rgba(212,212,216,0.1)'}`,
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-off-white text-sm">{tier.name}</span>
                    <div className="flex items-center gap-3">
                      <span className="mono text-[10px]" style={{ color: '#A8A9AD' }}>{tier.duration}</span>
                      <span className="mono text-[11px] font-semibold" style={{ color: '#2D5F9A' }}>{tier.price}</span>
                    </div>
                  </div>
                  <p className="text-xs" style={{ color: 'rgba(168,169,173,0.7)' }}>{tier.desc}</p>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm mb-4" style={{ color: '#A8A9AD' }}>Choose your preferred drop-off date.</p>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 rounded-lg text-sm text-off-white outline-none"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(212,212,216,0.12)',
                  colorScheme: 'dark',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(45,95,154,0.6)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(212,212,216,0.12)')}
              />
              <p className="mono text-[10px] tracking-wider" style={{ color: 'rgba(168,169,173,0.4)' }}>
                Mon–Fri drop-off · We&apos;ll confirm availability within 2 hours
              </p>
            </div>
          )}
        </div>

        {/* Footer CTA */}
        {!submitted && (
          <div className="px-6 pb-6">
            <button
              onClick={handleSubmit}
              disabled={!canProceed()}
              className="w-full btn-sapphire py-4 rounded-full mono text-[11px] font-semibold tracking-widest uppercase text-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-sapphire disabled:hover:shadow-none"
            >
              {step < 2 ? 'Continue' : 'Confirm Reservation'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}